import React, { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { encadrantRoleFor, canViewAccountTrainingData } from "../lib/encadrant";
import { usePilotScope } from "../lib/pilot-scope";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft,
  Calendar,
  Clock,
  BarChart3,
  ChevronRight,
  FileText,
  Phone,
  MessageCircle,
  GraduationCap,
  ArrowDown,
  CheckCircle,
  ClipboardList,
  Award,
  Trophy,
  Trash2,
} from "lucide-react";
import type { SessionSummary, SessionDetail as SessionDetailType, Feedback, ScoringCriterion, FormFieldSchema, PageType, Scenario } from "../types";
import { ScenarioBrief } from "../components/ScenarioBrief";
import { hasStructuredScenario, buildBriefFromVariables, applySpokenBrief } from "../lib/scenario-structured";
import { briefLabelsForLanguage } from "../lib/scenario-brief-labels";
import { localizeCompanySize, localizeIndustry } from "../lib/target-options-i18n";
import { localizePersonaOption } from "../lib/persona-options-i18n";
import { sessionsApi, accountApi, adminSessionsHistoryApi, trainingApi } from "../lib/api";
import { pickScoringLabel, pickVerdictLabel } from "../lib/scoring-i18n";
import { DrillIcon } from "../components/DrillIcon";
import { DrillHistoryView } from "../components/DrillHistoryView";
import { drillSessionApi } from "../lib/api";
import { launchDrill, lowest3Drillable } from "../lib/drill-launch";
import { useTranslation, Trans } from "react-i18next";
import { formatDateLong, formatDateShort } from "../i18n/formatters";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { useDrillVisible } from "../hooks/useDrillVisible";

interface HistoryPageProps {
  user: { id: string; profileType: string; clientAccountId?: string };
  onBack: () => void;
  initialSessionId?: string | null;
  // Onglet initial à ouvrir (utilisé quand on revient depuis SalesCoachPage)
  initialHistoryTab?: "training" | "prep-meeting";
  // Navigation vers la page Sales Coach Réel (intégration via tab en haut)
  onNavigateToSalesCoach?: () => void;
  // Navigation générique (ex. « Voir le parcours » depuis les onglets)
  onNavigate?: (page: PageType) => void;
  // Ouvre un challenge précis (« Voir le challenge » depuis l'historique)
  onOpenChallenge?: (challengeId: string) => void;
  // Reprise d'une fiche Prep Meeting (brouillon ou finalisée) depuis l'historique
  onOpenPrepMeeting?: (sessionId: string) => void;
  // Mode embarqué (dans SessionsWorkspace) : masque l'onglet Prep Meeting et le
  // lien Sales Coach (ces vues sont gérées par la page Sessions live).
  embedded?: boolean;
  // Force un onglet unique et masque la barre d'onglets (ex. "training" pour
  // Sessions training, "parcours"/"challenge" pour leurs pages dédiées).
  onlyTab?: "training" | "parcours" | "challenge";
  /**
   * Mode « historique de l'équipe » (Pilot › Activité) : la liste s'ouvre sur toute
   * l'équipe du périmètre, avec un sélecteur par personne. Sans ce mode, l'écran
   * Play ne montre que SES sessions — un encadrant y trouve un lien vers Pilot.
   */
  teamScope?: boolean;
  /** Play, encadrant : « Les sessions de ton équipe se lisent dans Pilot ». */
  onOpenTeamHistory?: () => void;
}

export interface SessionWithDetails {
  id: string;
  agentName: string;
  agentIcon: string;
  simulatorId: string;
  simulatorType: "training" | "coach";
  createdAt: string;
  duration: number;
  transcript: string[];
  scenarioMarkdown: string;
  /** Scénario complet (colonnes + snapshot `variables`) — permet d'afficher le brief en
   *  sections comme à l'entraînement. Volontairement la SEULE source pour l'historique :
   *  relire le cas de simulation viderait rétroactivement les sessions dont le cas a été
   *  supprimé (c'est un vrai DELETE), et le modifier réécrirait le passé. */
  scenario?: Scenario | null;
  coachTranscript: string[];
  feedback?: Feedback;
  scoringCriteria?: ScoringCriterion[];
  // Coach-specific fields
  formData?: Record<string, unknown>;
  resultSections?: Array<{ title: string; content: string }>;
  chatMessages?: Array<{ role: string; content: string }>;
}

// Score criteria now come from the simulator's scoringCriteria (dynamic per agent)

function getTotalScoreColor(total: number) {
  if (total >= 41) return "text-kestio-green";
  if (total >= 21) return "text-kestio-yellow";
  return "text-kestio-coral";
}

function getIndicatorScoreColor(score: number) {
  if (score >= 7) return "text-kestio-green";
  if (score >= 4) return "text-kestio-yellow";
  return "text-kestio-coral";
}

function renderMarkdown(md: string) {
  const lines = md.split("\n");
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listOrdered = false;

  const flushList = () => {
    if (listItems.length > 0) {
      const Tag = listOrdered ? "ol" : "ul";
      elements.push(
        <Tag key={elements.length} className={`text-sm text-muted-foreground ml-4 space-y-1 ${listOrdered ? "list-decimal" : "list-disc"}`}>
          {listItems.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
        </Tag>
      );
      listItems = [];
    }
  };

  const renderInline = (text: string): React.ReactNode => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} className="text-foreground font-medium">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "") {
      flushList();
      continue;
    }
    if (trimmed.startsWith("### ")) {
      flushList();
      elements.push(<h4 key={elements.length} className="font-medium text-sm mt-4 mb-1">{trimmed.slice(4)}</h4>);
    } else if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(<h3 key={elements.length} className="font-semibold text-base mt-4 mb-2">{trimmed.slice(3)}</h3>);
    } else if (trimmed.startsWith("- ")) {
      listOrdered = false;
      listItems.push(trimmed.slice(2));
    } else if (/^\d+\.\s/.test(trimmed)) {
      listOrdered = true;
      listItems.push(trimmed.replace(/^\d+\.\s/, ""));
    } else {
      flushList();
      elements.push(<p key={elements.length} className="text-sm text-muted-foreground">{renderInline(trimmed)}</p>);
    }
  }
  flushList();
  return <div className="space-y-1">{elements}</div>;
}

function TranscriptView({
  lines,
  otherLabel,
  youLabel,
}: {
  lines: string[];
  otherLabel: string;
  youLabel: string;
}) {
  return (
    <div className="space-y-3">
      {lines.map((line, index) => {
        const isUser = line.startsWith("Vous:");
        const label = isUser ? youLabel : otherLabel;
        const bubbleClass = isUser ? "bg-kestio-blue text-white" : "bg-muted";
        return (
          <div
            key={index}
            className={`flex ${isUser ? "justify-end" : "justify-start"}`}
          >
            <div className={`max-w-[85%] rounded-lg px-4 py-2 ${bubbleClass}`}>
              <p className="text-xs font-medium mb-0.5 opacity-70">
                {label}
              </p>
              <p className="text-sm">{line.replace(/^(Agent:|Coach:|Vous:)\s*/, "")}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CoachFormDataView({ formData }: { formData: Record<string, unknown> }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {Object.entries(formData).map(([key, value]) => {
        if (!value || (typeof value === "string" && !value.trim())) return null;
        // Display the key as a readable label
        const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
        const displayValue = typeof value === "boolean"
          ? (value ? t("history.yes") : t("history.no"))
          : String(value);
        return (
          <div key={key} className="border-b border-border/50 pb-2">
            <p className="text-xs font-medium text-muted-foreground mb-0.5">{label}</p>
            <p className="text-sm whitespace-pre-wrap">{displayValue}</p>
          </div>
        );
      })}
    </div>
  );
}

function CoachResultsView({ sections }: { sections: Array<{ title: string; content: string }> }) {
  return (
    <div className="space-y-6">
      {sections.map((section, i) => (
        <div key={i}>
          <h3 className="font-semibold text-base mb-2 text-kestio-blue">{section.title}</h3>
          {renderMarkdown(section.content)}
        </div>
      ))}
    </div>
  );
}

function CoachChatView({ messages, youLabel }: { messages: Array<{ role: string; content: string }>; youLabel: string }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {messages.map((msg, index) => {
        const isUser = msg.role === "user";
        return (
          <div key={index} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-lg px-4 py-2 ${isUser ? "bg-kestio-blue text-white" : "bg-muted"}`}>
              <p className="text-xs font-medium mb-0.5 opacity-70">
                {isUser ? youLabel : t("history.coach")}
              </p>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SessionDetailView({
  session,
  onBack,
  showBackButton,
  onNavigate,
}: {
  session: SessionWithDetails;
  onBack?: () => void;
  showBackButton?: boolean;
  onNavigate?: (page: PageType) => void; // « S'exercer » : route vers la page d'exercice
}) {
  const { t } = useTranslation();
  const isCoach = session.simulatorType === "coach";
  const [activeTab, setActiveTab] = useState(isCoach ? "results" : "scenario");

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const { i18n } = useTranslation();
  const lang = i18n.language;
  const getLabel = (c: { label?: string; labelFr?: string; labelEn?: string }) => pickScoringLabel(c, lang);
  const overallKeys = ["overall", "global"];
  // Filtre : on n'affiche QUE les critères réellement évalués (présents dans feedback.scores)
  // Les critères non sélectionnés au moment de la session ne doivent pas apparaître à 0/10
  const scores = session.feedback?.scores;
  const isEvaluated = (key: string) =>
    !scores || Object.prototype.hasOwnProperty.call(scores, key);
  const scoringCriteria = (session.scoringCriteria || []).filter(c => c.type === "score" && isEvaluated(c.key));
  const scoringCriteriaForTotal = scoringCriteria.filter(c => !overallKeys.includes(c.key));
  const booleanCriteria = (session.scoringCriteria || []).filter(c => c.type === "boolean" && isEvaluated(c.key));

  // « S'exercer » : icône sur les 3 critères à plus forte marge de progression AYANT une fiche
  // active. Depuis l'historique → décompté (origin=history).
  const drillSimulatorId = (session as { simulatorId?: string }).simulatorId;
  const [drillEligibleKeys, setDrillEligibleKeys] = useState<Set<string>>(new Set());
  // Seconde protection de l'écran (13/10/2026) : S'exercer fermé → aucune icône, même si le serveur en servait une.
  const drillOuvert = useDrillVisible();
  useEffect(() => {
    if (!drillOuvert || !onNavigate || !drillSimulatorId) { setDrillEligibleKeys(new Set()); return; }
    let cancelled = false;
    drillSessionApi.eligibleCriteria(drillSimulatorId)
      .then((rows) => { if (!cancelled) setDrillEligibleKeys(new Set(rows.map((r) => r.criterionKey))); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [drillOuvert, onNavigate, drillSimulatorId]);
  const drillableKeys = lowest3Drillable(
    scoringCriteriaForTotal.map((c) => ({ key: c.key, score: Number(session.feedback?.scores?.[c.key]) || 0 })),
    drillEligibleKeys,
  );
  const launchDrillFromHistory = (c: { key: string; label?: string; labelFr?: string; labelEn?: string }) => {
    if (!onNavigate || !drillSimulatorId) return;
    launchDrill({ criterionKey: c.key, criterionLabel: getLabel(c), simulatorId: drillSimulatorId, origin: "history" }, onNavigate);
  };

  const trainingTabs = [
    { value: "scenario", label: t("history.scenarioTab"), icon: FileText },
    { value: "call", label: t("history.callTab"), icon: Phone },
    { value: "scoring", label: t("history.scoringTab"), icon: BarChart3 },
    { value: "feedback", label: t("history.feedbackTab"), icon: MessageCircle },
    { value: "coaching", label: t("history.coachingTab"), icon: GraduationCap },
  ];

  const coachTabs = [
    { value: "form", label: t("history.formTab"), icon: ClipboardList },
    { value: "results", label: t("history.resultsTab"), icon: FileText },
    { value: "chat", label: t("history.chatTab"), icon: MessageCircle },
  ];

  const tabs = isCoach ? coachTabs : trainingTabs;

  return (
    <Card>
      <CardHeader>
        {/* Mobile: bouton retour */}
        {showBackButton && onBack && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="w-fit -ml-2 mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            {t("history.backToList")}
          </Button>
        )}

        {/* En-tête session */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AgentIcon icon={session.agentIcon} size="lg" />
            <div>
              <CardTitle>{session.agentName}</CardTitle>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {formatDateLong(session.createdAt)}
                </span>
                {!isCoach && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {formatDuration(session.duration)}
                  </span>
                )}
                {isCoach && session.chatMessages && session.chatMessages.length > 0 && (
                  <span className="flex items-center gap-1">
                    <MessageCircle className="h-4 w-4" />
                    {session.chatMessages.length} {t("history.chatMessages")}
                  </span>
                )}
              </div>
            </div>
          </div>
          {!isCoach && (
            <div className="text-right">
              <div className={`text-2xl sm:text-3xl font-bold ${getTotalScoreColor(session.feedback?.totalScore ?? 0)}`}>
                {session.feedback?.totalScore ?? "—"}/{scoringCriteriaForTotal.length * 10}
              </div>
              {booleanCriteria.map(c => {
                const val = session.feedback?.scores?.[c.key];
                return val ? (
                  <Badge key={c.key} className="bg-kestio-green-bg text-white mt-1"><CheckCircle className="h-3 w-3 mr-1" />{getLabel(c)}</Badge>
                ) : null;
              })}
            </div>
          )}
          {isCoach && (
            <Badge variant="secondary" className="text-kestio-blue border-kestio-blue/30">{t("history.coach")}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="flex-1 gap-1.5 px-1 sm:px-3">
                <tab.icon className="h-4 w-4 shrink-0" />
                <span className="hidden sm:inline text-xs">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* === COACH TABS === */}
          {isCoach && (
            <>
              <TabsContent value="form" className="mt-4">
                {session.formData ? (
                  <CoachFormDataView formData={session.formData} />
                ) : (
                  <p className="text-sm text-muted-foreground">{t("history.noFormDataAvailable")}</p>
                )}
              </TabsContent>

              <TabsContent value="results" className="mt-4">
                {session.resultSections && session.resultSections.length > 0 ? (
                  <CoachResultsView sections={session.resultSections} />
                ) : (
                  <p className="text-sm text-muted-foreground">{t("history.noResultsAvailable")}</p>
                )}
              </TabsContent>

              <TabsContent value="chat" className="mt-4">
                {session.chatMessages && session.chatMessages.length > 0 ? (
                  <CoachChatView messages={session.chatMessages} youLabel={t("history.you")} />
                ) : (
                  <p className="text-sm text-muted-foreground">{t("history.noChatAvailable")}</p>
                )}
              </TabsContent>
            </>
          )}

          {/* === TRAINING TABS === */}
          {!isCoach && (
            <>
              <TabsContent value="scenario" className="mt-4">
                {/* Même présentation qu'à l'entraînement (composant partagé). Tout vient du
                    scénario FIGÉ et de son instantané `variables` : une session reste
                    lisible même si son cas de simulation a été modifié ou supprimé depuis.
                    ⚠️ Les sessions ANTÉRIEURES au chantier gardent le texte intégral SEUL,
                    sans les sections : les reconstituer par-dessus rejouerait le doublon
                    que ce chantier supprime (le texte répète offre, fonction et entreprise). */}
                {session.scenario && hasStructuredScenario(session.scenario) ? (() => {
                  const s = session.scenario!;
                  const brief = buildBriefFromVariables(s);
                  // Séance jouée dans une autre langue que l'interface d'alors : le brief
                  // s'affiche dans la langue d'entraînement, comme pendant l'entraînement —
                  // le récit de N8N (prospect, entreprise, contexte) y est rédigé une fois
                  // pour toutes, un habillage dans une autre langue rendrait l'écran bilingue.
                  const briefLang = s.spokenLanguage || null;
                  const shown = applySpokenBrief({
                    offerLabel: brief.offerLabel,
                    offerDescription: brief.offerDescription,
                    interlocutorSubtitle: localizePersonaOption(brief.interlocutorSubtitle, i18n.language),
                    personalityStyle: brief.personalityStyle,
                    industryLabel: brief.industryRaw ? localizeIndustry(brief.industryRaw, i18n.language) : "",
                    companySizeLabel: localizeCompanySize(brief.companySize, i18n.language),
                  }, briefLang ? s.briefSpoken : null);
                  return (
                    <div className="space-y-3">
                      {/* Le bloc « Ce cas insiste sur » reste masqué ici : `focusCriteria`
                          n'est pas passé. L'historique lit l'INSTANTANÉ du scénario
                          (`variables`), et les critères sur-pondérés n'y figurent pas — les
                          relire sur le cas afficherait ceux d'AUJOURD'HUI sur une session
                          d'il y a trois mois. */}
                      <ScenarioBrief
                        prospectName={s.prospectName}
                        companyName={s.companyName}
                        companyDescription={s.prospectCompanyDescription}
                        priorContext={s.priorContext}
                        legacyFullText={session.scenarioMarkdown}
                        offerLabel={shown.offerLabel}
                        offerDescription={shown.offerDescription}
                        offerCount={shown.offerLabel ? 1 : 0}
                        interlocutorSubtitle={shown.interlocutorSubtitle}
                        personalityStyle={shown.personalityStyle}
                        companyMeta={[shown.industryLabel, shown.companySizeLabel].filter(Boolean).join(" · ")}
                        isB2C={brief.isB2C}
                        labels={briefLabelsForLanguage(
                          briefLang ?? i18n.language,
                          i18n.getFixedT(briefLang === "fr" || briefLang === "en" ? briefLang : i18n.language),
                        )}
                      />
                    </div>
                  );
                })() : session.scenarioMarkdown ? renderMarkdown(session.scenarioMarkdown) : (
                  <p className="text-sm text-muted-foreground">{t("history.noScenarioAvailable")}</p>
                )}
              </TabsContent>

              <TabsContent value="call" className="mt-4">
                {session.transcript.length > 0 ? (
                  <TranscriptView lines={session.transcript} otherLabel={t("training.prospect")} youLabel={t("history.you")} />
                ) : (
                  <p className="text-sm text-muted-foreground">{t("history.noTranscriptAvailable")}</p>
                )}
              </TabsContent>

              <TabsContent value="scoring" className="mt-4">
                {session.feedback ? (
                  <div className="space-y-4">
                    {scoringCriteriaForTotal.map((c) => {
                      const score = Number(session.feedback?.scores?.[c.key]) || 0;
                      const m = c.max || 10;
                      return (
                        <div key={c.key}>
                          <div className="flex justify-between items-center text-sm mb-1 gap-2">
                            <span className="flex items-center gap-1.5">
                              {getLabel(c)}
                              {drillableKeys.has(c.key) && (
                                <DrillIcon onClick={() => launchDrillFromHistory(c)} tooltip={t("drill.iconTooltip")} size={13} />
                              )}
                            </span>
                            <span className={`font-medium ${getIndicatorScoreColor(score)}`}>
                              {score}/{m}
                            </span>
                          </div>
                          <Progress
                            value={(score / m) * 100}
                            className="h-2"
                          />
                        </div>
                      );
                    })}
                    {scoringCriteria.filter(c => overallKeys.includes(c.key)).map((c) => {
                      const score = Number(session.feedback?.scores?.[c.key]) || 0;
                      const m = c.max || 10;
                      const passed = c.successThreshold != null && score >= c.successThreshold;
                      const hasThreshold = c.successThreshold != null;
                      const verdictLabel = hasThreshold
                        ? passed
                          ? pickVerdictLabel(c, lang, true) || t("history.passed")
                          : pickVerdictLabel(c, lang, false) || t("history.failed")
                        : null;
                      return (
                        <div key={c.key} className="border-t pt-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="italic text-kestio-blue">{getLabel(c)}</span>
                            <span className="font-medium text-kestio-blue">
                              {score}/{m}
                            </span>
                          </div>
                          <Progress
                            value={(score / m) * 100}
                            className="h-2"
                            indicatorClassName="bg-kestio-blue"
                          />
                          {verdictLabel && (
                            <div className={`mt-2 text-center text-xs font-semibold px-3 py-1.5 rounded-md ${passed ? "bg-kestio-green-bg/10 text-kestio-green" : "bg-kestio-coral-bg/10 text-kestio-coral"}`}>
                              {passed ? "✓" : "✗"} {verdictLabel}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("history.noScoringAvailable")}</p>
                )}
              </TabsContent>

              <TabsContent value="feedback" className="mt-4">
                {session.feedback ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      {booleanCriteria.map(c => {
                        const val = session.feedback?.scores?.[c.key];
                        return val ? (
                          <Badge key={c.key} className="bg-kestio-green-bg text-white"><CheckCircle className="h-3 w-3 mr-1" />{getLabel(c)}</Badge>
                        ) : (
                          <Badge key={c.key} variant="secondary">{t("history.scoreCriterionNo", { label: getLabel(c) })}</Badge>
                        );
                      })}
                      <span className={`text-lg font-bold ${getTotalScoreColor(session.feedback?.totalScore ?? 0)}`}>
                        {session.feedback?.totalScore}/{scoringCriteriaForTotal.length * 10}
                      </span>
                    </div>
                    <Separator />
                    <div>
                      <h4 className="font-medium mb-2">{t("history.coachFeedback")}</h4>
                      {renderMarkdown(session.feedback?.content || "")}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("history.noFeedbackAvailable")}</p>
                )}
              </TabsContent>

              <TabsContent value="coaching" className="mt-4">
                {session.coachTranscript.length > 0 ? (
                  <TranscriptView lines={session.coachTranscript} otherLabel={t("history.coach")} youLabel={t("history.you")} />
                ) : (
                  <p className="text-sm text-muted-foreground">{t("history.noCoachingAvailable")}</p>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
}

export function transformDetail(detail: SessionDetailType): SessionWithDetails {
  const isCoach = detail.simulatorType === "coach";
  const allMessages = detail.messages || [];

  // Duration in seconds
  const duration = (detail.durationInMinutes || 0) * 60;

  if (isCoach) {
    // Coach sessions: first user msg = formData (JSON), first coach msg = results (JSON sections), rest = Q&A chat
    let formData: Record<string, unknown> | undefined;
    let resultSections: Array<{ title: string; content: string }> | undefined;
    const chatMessages: Array<{ role: string; content: string }> = [];

    let foundFormData = false;
    let foundResults = false;

    for (const m of allMessages) {
      if (m.role === "user" && !foundFormData) {
        // First user message = form data (JSON)
        try {
          const parsed = JSON.parse(m.content);
          if (typeof parsed === "object" && !Array.isArray(parsed)) {
            formData = parsed;
            foundFormData = true;
            continue;
          }
        } catch { /* not JSON, treat as chat */ }
      }
      if (m.role === "coach" && !foundResults) {
        // First coach message = results sections (JSON)
        try {
          const parsed = JSON.parse(m.content);
          if (parsed?.sections && Array.isArray(parsed.sections)) {
            resultSections = parsed.sections;
            foundResults = true;
            continue;
          }
        } catch { /* not JSON, treat as chat */ }
      }
      // Remaining messages = Q&A chat
      if (m.role === "user" || m.role === "coach") {
        chatMessages.push({ role: m.role, content: m.content });
      }
    }

    return {
      id: detail.id,
      agentName: detail.simulatorName,
      agentIcon: detail.simulatorIcon || "",
      simulatorId: detail.simulatorId,
      simulatorType: "coach",
      createdAt: detail.createdAt,
      duration,
      transcript: [],
      scenarioMarkdown: "",
      coachTranscript: [],
      feedback: undefined,
      scoringCriteria: undefined,
      formData,
      resultSections,
      chatMessages,
    };
  }

  // Training sessions: transcriptions séparées par phase (depuis 0083)
  // phase="call" → transcription de l'appel avec l'agent prospect
  // phase="coaching" → debrief vocal avec le coach
  // Fallback pour les messages historiques sans phase : role="coach" ⇒ coaching, sinon call
  const getPhase = (m: any): "call" | "coaching" =>
    (m.phase === "call" || m.phase === "coaching") ? m.phase : (m.role === "coach" ? "coaching" : "call");

  // Filtre volontaire (2026-07-27, validation Valérie) : les messages rôles
  // "chuchoteur" (consignes d'injection sideband) et "feedback" (notes de score
  // progressif) sont EXCLUS du transcript d'appel affiché au commercial —
  // c'est du bruit technique (ex: "Qualité des questions : 5 (+1)") sans intérêt
  // côté user. Ces messages restent persistés en DB (le workflow N8N les utilise
  // pour calculer l'état accumulé) et sont visibles via le visualiseur admin
  // des tables si besoin de debug.
  const transcript = allMessages
    .filter(
      (m) => (m.role === "agent" || m.role === "user") && getPhase(m) === "call",
    )
    .map((m) => {
      if (m.role === "user") return `Vous: ${m.content}`;
      return `Agent: ${m.content}`;
    });

  const coachTranscript = allMessages
    .filter((m) => getPhase(m) === "coaching")
    .map((m) => {
      if (m.role === "user") return `Vous: ${m.content}`;
      return `Coach: ${m.content}`;
    });

  const scenarioMarkdown = detail.scenario?.userMessage || detail.scenario?.raw || "";

  return {
    id: detail.id,
    agentName: detail.simulatorName,
    agentIcon: detail.simulatorIcon || "",
    simulatorId: detail.simulatorId,
    simulatorType: "training",
    createdAt: detail.createdAt,
    duration,
    transcript,
    scenarioMarkdown,
    scenario: detail.scenario || null,
    coachTranscript,
    feedback: detail.feedback || undefined,
    scoringCriteria: detail.scoringCriteria,
  };
}

// Render an agent icon: <img> if base64/URL, otherwise text (emoji)
function AgentIcon({ icon, size = "md" }: { icon: string; size?: "sm" | "md" | "lg" }) {
  const sizeClasses = { sm: "h-5 w-5", md: "h-8 w-8", lg: "h-10 w-10" };
  const textSizes = { sm: "text-base", md: "text-2xl", lg: "text-3xl" };
  if (icon && (icon.startsWith("data:") || icon.startsWith("http"))) {
    return <img src={icon} alt="" loading="lazy" className={`${sizeClasses[size]} rounded object-contain`} />;
  }
  return <span className={textSizes[size]}>{icon || "?"}</span>;
}

export function HistoryPage({ user, onBack, initialSessionId, initialHistoryTab, onNavigateToSalesCoach, onNavigate, onOpenChallenge, onOpenPrepMeeting, embedded, onlyTab, teamScope, onOpenTeamHistory }: HistoryPageProps) {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [selectedDetail, setSelectedDetail] = useState<SessionWithDetails | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  // Un échec de chargement doit SE VOIR. Il était avalé dans un `console.error` : le clic
  // sur une séance ne produisait rien du tout, sans le moindre message.
  const [detailError, setDetailError] = useState<string | null>(null);
  // Mobile : null = liste, session = détail
  const [mobileDetail, setMobileDetail] = useState<SessionWithDetails | null>(null);
  // Ref sur le container du détail (desktop) — utilisée par scrollToDetail() pour
  // ramener l'user en haut de la colonne détail quand il clique une session depuis
  // une liste scrollée bas (bug Mariam 06/07/26).
  const detailContainerRef = useRef<HTMLDivElement>(null);
  const scrollToDetail = () => {
    detailContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const [agentFilter, setAgentFilter] = useState<string>("all");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  // Onglet actif : "training" (sessions de simulation) | "prep-meeting" (fiches prep)
  const [historyTab, setHistoryTab] = useState<"training" | "prep-meeting" | "parcours" | "challenge" | "exercises">(onlyTab ?? initialHistoryTab ?? "training");
  // « S'exercer » fermé (interrupteur, `drill-visibility.ts`) : onglets « Historique des exercices » masqués.
  const drillVisible = useDrillVisible();
  useEffect(() => {
    if (!drillVisible && historyTab === "exercises") setHistoryTab(onlyTab ?? "training");
  }, [drillVisible, historyTab, onlyTab]);
  // Liste des fiches Prep Meeting du user
  const [prepMeetingItems, setPrepMeetingItems] = useState<Array<{
    id: string;
    createdAt: string;
    updatedAt: string;
    status: "draft" | "completed";
    companyName: string | null;
    contactName: string | null;
    contactRole: string | null;
    meetingObjective: string | null;
  }>>([]);
  const [loadingPrepMeeting, setLoadingPrepMeeting] = useState(false);

  // Play = ses propres sessions. Le sélecteur de collaborateur ne reste, côté Play, qu'au RH (profil
  // sans espace Pilot qui voit l'entraînement de son compte, règle partagée `canViewAccountTrainingData`) ;
  // les encadrants l'ont dans Pilot › Activité ; le GES n'a aucun accès à l'entraînement d'autrui
  // (décision de Valérie du 29/09/2026).
  const hasPilot = !!encadrantRoleFor(user.profileType);
  const canViewAccount = teamScope ? true : (canViewAccountTrainingData(user.profileType) && !hasPilot);
  const [userFilter, setUserFilter] = useState<string>(teamScope ? "all" : "mine");
  const [accountUsers, setAccountUsers] = useState<{ id: string; firstName: string; lastName: string; managerId?: string | null }[]>([]);
  const pilot = usePilotScope();
  /**
   * Mode équipe : les personnes viennent du périmètre SERVI par le serveur (`/api/sessions?team=1`
   * renvoie `people`, calculé par `teamHistoryUserIds`) — et non plus de la liste des collaborateurs
   * du compte du lecteur : un COO placé chez un client y voyait ses collègues Kestio, donc un
   * historique vide (audit du lot 5). Le serveur applique déjà compte, équipe et règle C.
   */
  const [teamPeople, setTeamPeople] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [teamSessionsError, setTeamSessionsError] = useState(false);
  const [teamSessionsReload, setTeamSessionsReload] = useState(0);
  const scopedUsers = useMemo(
    () => (teamScope ? teamPeople : accountUsers.filter((u) => u.id !== user.id)),
    [teamScope, teamPeople, accountUsers, user.id],
  );

  // Kestio admin filters (ADM/COO) — pattern Reporting "Dashboard"
  // En mode embarqué (Sessions training / Parcours / Challenges), on affiche
  // TOUJOURS l'historique personnel du user — jamais la vue d'inspection admin
  // (qui exige un compte sélectionné et renverrait une liste vide).
  const isKestioAdmin = (user.profileType === "ADM" || user.profileType === "COO") && !embedded;
  const [adminAccounts, setAdminAccounts] = useState<{ id: string; name: string }[]>([]);
  const [adminAccountId, setAdminAccountId] = useState<string>("");
  const [adminUsers, setAdminUsers] = useState<{ id: string; firstName: string | null; lastName: string | null; email: string }[]>([]);
  const [adminUserId, setAdminUserId] = useState<string>("__all__");
  const [adminSimulators, setAdminSimulators] = useState<{ id: string; name: string; sessionCount: number }[]>([]);
  const [adminSimulatorId, setAdminSimulatorId] = useState<string>("__all__");
  const [adminFromDate, setAdminFromDate] = useState<string>("");
  const [adminToDate, setAdminToDate] = useState<string>("");

  // Cache for loaded details
  const detailCache = useRef<Map<string, SessionWithDetails>>(new Map());

  // Load account users for filter dropdown (hors mode équipe : le RH dans Play)
  useEffect(() => {
    if (canViewAccount && !teamScope) {
      accountApi.getCollaborators()
        .then((collabs: any[]) => setAccountUsers(collabs.map((c: any) => ({
          id: c.id,
          firstName: c.firstName || c.first_name || "",
          lastName: c.lastName || c.last_name || "",
          managerId: c.managerId ?? c.manager_id ?? null,
        }))))
        .catch(() => {});
    }
  }, [canViewAccount, teamScope]);

  // Préparations de l'ÉQUIPE : rechargées quand le périmètre ou la personne choisie change.
  const [teamPrepError, setTeamPrepError] = useState(false);
  /** Préparation d'autrui ouverte en lecture seule (décision D5). */
  const [teamPrepOpen, setTeamPrepOpen] = useState<string | null>(null);
  const [teamPrepReload, setTeamPrepReload] = useState(0);
  useEffect(() => {
    if (!teamScope || historyTab !== "prep-meeting") return;
    let live = true;
    setLoadingPrepMeeting(true);
    setTeamPrepError(false);
    trainingApi.listPrepMeetingHistory({ team: true, userId: userFilter === "all" ? undefined : userFilter })
      .then((items) => { if (live) setPrepMeetingItems(items); })
      // Un échec se DIT (audit du lot 5) : il s'affichait comme « aucune préparation ».
      .catch(() => { if (live) { setPrepMeetingItems([]); setTeamPrepError(true); } })
      .finally(() => { if (live) setLoadingPrepMeeting(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamScope, historyTab, userFilter, pilot.account, pilot.scope, teamPrepReload]);

  // Auto-fetch Prep Meeting list quand on arrive directement sur cet onglet
  // (via initialHistoryTab depuis SalesCoachPage)
  useEffect(() => {
    if (historyTab === "prep-meeting" && prepMeetingItems.length === 0 && !loadingPrepMeeting) {
      setLoadingPrepMeeting(true);
      trainingApi.listPrepMeetingHistory()
        .then(setPrepMeetingItems)
        .catch((err) => console.error("Erreur chargement Prep Meeting history:", err))
        .finally(() => setLoadingPrepMeeting(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kestio admin: load client accounts list + auto-select first
  useEffect(() => {
    if (!isKestioAdmin) return;
    adminSessionsHistoryApi.getAccounts()
      .then((accounts) => {
        setAdminAccounts(accounts);
        if (accounts.length > 0 && !adminAccountId) setAdminAccountId(accounts[0]!.id);
      })
      .catch((err: Error) => console.error("Erreur chargement comptes:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKestioAdmin]);

  // Kestio admin: cascade compte → users
  useEffect(() => {
    if (!isKestioAdmin || !adminAccountId) return;
    setAdminUsers([]);
    setAdminUserId("__all__");
    setAdminSimulators([]);
    setAdminSimulatorId("__all__");
    adminSessionsHistoryApi.getUsers(adminAccountId)
      .then(setAdminUsers)
      .catch((err: Error) => console.error("Erreur chargement utilisateurs:", err));
  }, [isKestioAdmin, adminAccountId]);

  // Kestio admin: cascade user → simulateurs (avec compteur)
  useEffect(() => {
    if (!isKestioAdmin || adminUserId === "__all__" || !adminUserId) {
      setAdminSimulators([]);
      setAdminSimulatorId("__all__");
      return;
    }
    adminSessionsHistoryApi.getSimulators(adminUserId)
      .then(setAdminSimulators)
      .catch((err: Error) => console.error("Erreur chargement simulateurs:", err));
  }, [isKestioAdmin, adminUserId]);

  const loadSessions = (filterUserId?: string) => {
    // Mode Kestio admin : filtres compte/user/simulateur/dates
    if (isKestioAdmin) {
      if (!adminAccountId) {
        setSessions([]);
        setLoadingSessions(false);
        return;
      }
      setLoadingSessions(true);
      adminSessionsHistoryApi.getSessions({
        accountId: adminAccountId,
        userId: adminUserId !== "__all__" ? adminUserId : undefined,
        simulatorId: adminSimulatorId !== "__all__" ? adminSimulatorId : undefined,
        from: adminFromDate ? new Date(adminFromDate).toISOString() : undefined,
        to: adminToDate ? new Date(adminToDate + "T23:59:59").toISOString() : undefined,
      })
        .then((data: SessionSummary[]) => {
          setSessions(data);
          setSelectedDetail(null);
          if (initialSessionId) loadDetail(initialSessionId);
        })
        .catch((err: Error) => console.error("Erreur chargement sessions:", err))
        .finally(() => setLoadingSessions(false));
      return;
    }

    // Mode équipe (Pilot › Activité) : sessions ET personnes servies par le serveur ; un échec se
    // dit, avec « Réessayer » (audit du lot 5 : il s'affichait comme un historique vide).
    if (teamScope) {
      setLoadingSessions(true);
      setTeamSessionsError(false);
      sessionsApi.getTeamSessions(filterUserId && filterUserId !== "mine" ? filterUserId : "all")
        .then(({ sessions: data, people }) => {
          setSessions(data);
          setTeamPeople(people.map((p) => ({ id: p.id, firstName: p.name, lastName: "" })));
          if (initialSessionId) loadDetail(initialSessionId);
        })
        .catch(() => { setSessions([]); setTeamSessionsError(true); })
        .finally(() => setLoadingSessions(false));
      return;
    }

    // Mode standard
    setLoadingSessions(true);
    const uid = filterUserId === "all" ? "all" : filterUserId === "mine" ? undefined : filterUserId;
    sessionsApi.getSessions(undefined, uid)
      .then((data: SessionSummary[]) => {
        setSessions(data);
        if (initialSessionId) {
          loadDetail(initialSessionId);
        }
      })
      .catch((err: Error) => {
        console.error("Erreur chargement sessions:", err);
      })
      .finally(() => setLoadingSessions(false));
  };

  useEffect(() => {
    if (isKestioAdmin) {
      loadSessions();
    } else {
      loadSessions(userFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter, isKestioAdmin, adminAccountId, adminUserId, adminSimulatorId, adminFromDate, adminToDate, teamScope ? pilot.scope : null, teamScope ? pilot.account : null, teamSessionsReload]);

  const loadDetail = async (sessionId: string, mobile = false) => {
    // Check cache
    const cached = detailCache.current.get(sessionId);
    if (cached) {
      setDetailError(null);
      if (mobile) setMobileDetail(cached);
      else setSelectedDetail(cached);
      return;
    }

    setLoadingDetail(true);
    setDetailError(null);
    try {
      const detail: SessionDetailType = await sessionsApi.getSessionDetail(sessionId);
      const transformed = transformDetail(detail);
      detailCache.current.set(sessionId, transformed);
      if (mobile) setMobileDetail(transformed);
      else setSelectedDetail(transformed);
    } catch (err: any) {
      console.error("Erreur chargement détail session:", err);
      setDetailError(err?.message || t("history.detailError"));
    } finally {
      setLoadingDetail(false);
    }
  };


  // Derive agent options from loaded sessions
  const agentOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; icon: string }>();
    for (const s of sessions) {
      if (!map.has(s.simulatorId)) {
        map.set(s.simulatorId, { id: s.simulatorId, name: s.simulatorName, icon: s.simulatorIcon });
      }
    }
    return Array.from(map.values());
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    let filtered = agentFilter === "all"
      ? [...sessions]
      : sessions.filter((s) => s.simulatorId === agentFilter);
    // Catégorie selon l'onglet : libre (ni challenge ni parcours), parcours, challenge.
    if (historyTab === "training") filtered = filtered.filter((s) => !s.challengeId && !s.pathPhaseId);
    else if (historyTab === "parcours") filtered = filtered.filter((s) => !!s.pathPhaseId);
    else if (historyTab === "challenge") filtered = filtered.filter((s) => !!s.challengeId);
    filtered.sort((a, b) => {
      const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortOrder === "asc" ? diff : -diff;
    });
    return filtered;
  }, [sessions, agentFilter, sortOrder, historyTab]);

  // Auto-sélection du détail (desktop) : 1ère session DE L'ONGLET COURANT.
  useEffect(() => {
    if (!loadingSessions && filteredSessions.length > 0 && !selectedDetail && !initialSessionId) {
      loadDetail(filteredSessions[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSessions, filteredSessions]);

  // Regroupement par contexte (parcours / challenge) pour les onglets dédiés.
  const contextGroups = useMemo(() => {
    if (historyTab !== "parcours" && historyTab !== "challenge") return [];
    const map = new Map<string, SessionSummary[]>();
    for (const s of filteredSessions) {
      const key = historyTab === "challenge"
        ? (s.challengeTitle || s.challengeId || "—")
        : (s.pathTitleFr || s.pathTitleEn || "—");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return [...map.entries()];
  }, [filteredSessions, historyTab]);

  const formatDate = formatDateShort;

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return "—";
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Loading state
  if (loadingSessions) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-muted-foreground">{t("history.loadingHistory")}</p>
      </div>
    );
  }

  // Empty state
  const isFilteringOtherUser = canViewAccount && userFilter !== "mine";
  const noSessionMessage = isFilteringOtherUser
    ? t("history.noSessionForOtherUser")
    : historyTab === "parcours"
      ? t("history.noParcoursSession")
      : historyTab === "challenge"
        ? t("history.noChallengeSession")
        : `${t("history.noSessionYet")}`;
  const noSessionSubMessage = isFilteringOtherUser || historyTab === "parcours" || historyTab === "challenge"
    ? ""
    : t("history.startFirstTraining");

  // Mobile : vue détail
  if (mobileDetail) {
    return (
      <div className="lg:hidden space-y-4">
        <SessionDetailView
          session={mobileDetail}
          onBack={() => setMobileDetail(null)}
          showBackButton
          onNavigate={onNavigate}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:space-y-6" data-tour="history-view">
      {/* Header */}
      <div>
        <div>
          <h1 className="text-lg lg:text-2xl font-bold">{t("history.title")}</h1>
          {filteredSessions.length > 0 && (
            <p className="text-muted-foreground text-sm hidden lg:block">
              {t("history.sessionsCompleted", { count: filteredSessions.length })}
            </p>
          )}
        </div>
      </div>

      {/* Bascule entre sessions de simulation, Sales Coach Réel, et Préparations RDV */}
      {!onlyTab && (
      <div className="flex gap-2 border-b flex-wrap">
        <button
          onClick={() => setHistoryTab("training")}
          className={`px-4 py-2 text-sm border-b-2 transition-colors ${
            historyTab === "training" ? "font-semibold border-current" : "font-medium text-gray-500 hover:text-gray-700 border-transparent"
          }`}
          style={historyTab === "training" ? { color: "#384872" } : undefined}
        >
          {t("history.tabFree")}
        </button>
        <button
          onClick={() => setHistoryTab("parcours")}
          className={`px-4 py-2 text-sm border-b-2 transition-colors ${
            historyTab === "parcours" ? "font-semibold border-current" : "font-medium text-gray-500 hover:text-gray-700 border-transparent"
          }`}
          style={historyTab === "parcours" ? { color: "#384872" } : undefined}
        >
          {t("history.tabParcours")}
        </button>
        <button
          onClick={() => setHistoryTab("challenge")}
          className={`px-4 py-2 text-sm border-b-2 transition-colors ${
            historyTab === "challenge" ? "font-semibold border-current" : "font-medium text-gray-500 hover:text-gray-700 border-transparent"
          }`}
          style={historyTab === "challenge" ? { color: "#384872" } : undefined}
        >
          {t("history.tabChallenge")}
        </button>
        {drillVisible && (
        <button
          onClick={() => setHistoryTab("exercises")}
          className={`px-4 py-2 text-sm border-b-2 transition-colors ${
            historyTab === "exercises" ? "font-semibold border-current" : "font-medium text-gray-500 hover:text-gray-700 border-transparent"
          }`}
          style={historyTab === "exercises" ? { color: "#384872" } : undefined}
        >
          {t("drill.histTabLabel")}
        </button>
        )}
        {onNavigateToSalesCoach && (
          <button
            onClick={onNavigateToSalesCoach}
            className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
          >
            <span className="inline-flex items-center gap-1.5">
              {t("history.tabSalesCoach")}
            </span>
          </button>
        )}
        {(!embedded || teamScope) && (
        <button
          onClick={() => {
            setHistoryTab("prep-meeting");
            // Charge la liste à la 1ère ouverture (mode équipe : effet dédié, qui suit le périmètre).
            if (!teamScope && prepMeetingItems.length === 0 && !loadingPrepMeeting) {
              setLoadingPrepMeeting(true);
              trainingApi.listPrepMeetingHistory()
                .then(setPrepMeetingItems)
                .catch((err) => console.error("Erreur chargement Prep Meeting history:", err))
                .finally(() => setLoadingPrepMeeting(false));
            }
          }}
          className={`px-4 py-2 text-sm border-b-2 transition-colors ${
            historyTab === "prep-meeting" ? "font-semibold border-current" : "font-medium text-gray-500 hover:text-gray-700 border-transparent"
          }`}
          style={historyTab === "prep-meeting" ? { color: "#384872" } : undefined}
        >
          <span className="inline-flex items-center gap-1.5">
            {t("history.tabPrepMeeting")}
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase" style={{ background: "#988EF2", color: "white" }}>
              {t("history.newBadge")}
            </span>
          </span>
        </button>
        )}
      </div>
      )}

      {/* Mode embarqué (onlyTab) : mini-bascule entre l'historique verrouillé et « Historique des exercices ». */}
      {onlyTab && drillVisible && (
        <div className="flex gap-2 border-b flex-wrap">
          <button
            onClick={() => setHistoryTab(onlyTab)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${historyTab !== "exercises" ? "font-semibold border-current" : "font-medium text-gray-500 hover:text-gray-700 border-transparent"}`}
            style={historyTab !== "exercises" ? { color: "#384872" } : undefined}
          >
            {onlyTab === "parcours" ? t("history.tabParcours") : onlyTab === "challenge" ? t("history.tabChallenge") : t("history.tabFree")}
          </button>
          <button
            onClick={() => setHistoryTab("exercises")}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${historyTab === "exercises" ? "font-semibold border-current" : "font-medium text-gray-500 hover:text-gray-700 border-transparent"}`}
            style={historyTab === "exercises" ? { color: "#384872" } : undefined}
          >
            {t("drill.histTabLabel")}
          </button>
        </div>
      )}

      {/* Liste Prep Meeting (si onglet actif) */}
      {historyTab === "prep-meeting" && teamScope && (
        // Équipe : lecture seule (reprendre ou supprimer la préparation d'autrui serait un geste impossible).
        teamPrepError ? (
          <div role="alert" className="flex flex-wrap items-center gap-3 py-6 text-sm" style={{ color: "#C0423F" }}>
            {t("common.loadError")}
            <button type="button" className="font-semibold underline" onClick={() => setTeamPrepReload((n) => n + 1)}>{t("common.retry")}</button>
          </div>
        ) : <>
          {/* Décision D5 (30/09/2026) : la préparation d'autrui s'OUVRE, en lecture seule (ni reprise,
              ni suppression, ni chat) — même règle d'accès que la liste, vérifiée par le serveur. */}
          <PrepMeetingHistoryList items={prepMeetingItems} loading={loadingPrepMeeting} team onOpen={(id) => setTeamPrepOpen(id)} />
          {teamPrepOpen && (
            <TeamPrepMeetingDialog
              sessionId={teamPrepOpen}
              ownerName={(prepMeetingItems.find((it) => it.id === teamPrepOpen) as { userName?: string } | undefined)?.userName ?? ""}
              onClose={() => setTeamPrepOpen(null)}
            />
          )}
        </>
      )}
      {historyTab === "prep-meeting" && !teamScope && (
        <PrepMeetingHistoryList
          items={prepMeetingItems}
          loading={loadingPrepMeeting}
          onOpen={(id) => onOpenPrepMeeting?.(id)}
          onDelete={async (id) => {
            // Suppression d'un brouillon depuis l'historique — hard delete côté backend.
            // Optimistic UI : on filtre localement en cas de succès ; en cas d'erreur
            // on ré-fetch pour être sûr de rester cohérent avec la BDD.
            try {
              await trainingApi.deletePrepMeeting(id);
              setPrepMeetingItems((prev) => prev.filter((it) => it.id !== id));
            } catch (err) {
              alert(err instanceof Error ? err.message : "Erreur suppression brouillon");
              trainingApi.listPrepMeetingHistory().then(setPrepMeetingItems).catch(() => {});
            }
          }}
        />
      )}

      {/* Contenu sessions (Libre / Parcours / Challenge) — même structure pour tous,
          masqué seulement pour l'onglet Prep Meeting. */}
      {historyTab === "exercises" && drillVisible && (
        <div className="mt-4">{teamScope
          ? <DrillHistoryView team userId={userFilter === "all" ? undefined : userFilter} />
          // Play : le RH (seul profil à sélecteur dans Play) choisit aussi « Tous les collaborateurs »
          // ou une personne pour les exercices (décision D4 du 30/09/2026).
          : <DrillHistoryView userId={canViewAccount && userFilter !== "mine" ? userFilter : undefined} />}</div>
      )}

      {historyTab !== "prep-meeting" && historyTab !== "exercises" && teamScope && teamSessionsError && (
        <div role="alert" className="flex flex-wrap items-center gap-3 py-6 text-sm" style={{ color: "#C0423F" }}>
          {t("common.loadError")}
          <button type="button" className="font-semibold underline" onClick={() => setTeamSessionsReload((n) => n + 1)}>{t("common.retry")}</button>
        </div>
      )}

      {historyTab !== "prep-meeting" && historyTab !== "exercises" && !(teamScope && teamSessionsError) && (<>

      {/* Filtres Kestio admin (ADM/COO) */}
      {isKestioAdmin && (
        <div className="bg-slate-50 border rounded-lg p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("history.clientAccount")}</Label>
            <Select value={adminAccountId} onValueChange={setAdminAccountId}>
              <SelectTrigger size="sm">
                <SelectValue placeholder={t("history.selectAccount")} />
              </SelectTrigger>
              <SelectContent>
                {adminAccounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t("history.user")}</Label>
            <Select value={adminUserId} onValueChange={setAdminUserId} disabled={!adminAccountId}>
              <SelectTrigger size="sm">
                <SelectValue placeholder={t("history.allUsers")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("history.allUsers")}</SelectItem>
                {adminUsers.map((u) => {
                  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
                  return (
                    <SelectItem key={u.id} value={u.id}>{name || u.email}</SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t("history.simulator")}</Label>
            <Select
              value={adminSimulatorId}
              onValueChange={setAdminSimulatorId}
              disabled={adminUserId === "__all__" || !adminUserId}
            >
              <SelectTrigger size="sm">
                <SelectValue placeholder={adminUserId === "__all__" ? t("history.selectUserFirst") : t("history.allSimulators")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("history.allSimulators")}</SelectItem>
                {adminSimulators.map((s) => (
                  <SelectItem key={s.id} value={s.id} disabled={s.sessionCount === 0}>
                    {s.name} ({s.sessionCount})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">{t("history.period")}</Label>
            <div className="flex gap-1.5 items-center">
              <Input
                type="date"
                value={adminFromDate}
                onChange={(e) => setAdminFromDate(e.target.value)}
                className="text-xs"
                aria-label={t("history.startDateLabel")}
              />
              <span className="text-muted-foreground text-xs">→</span>
              <Input
                type="date"
                value={adminToDate}
                onChange={(e) => setAdminToDate(e.target.value)}
                className="text-xs"
                aria-label={t("history.endDateLabel")}
              />
            </div>
          </div>
        </div>
      )}

      {/* Filtres & tri */}
      <div className="flex items-center gap-2 flex-wrap">
        {!isKestioAdmin && canViewAccount && scopedUsers.length > 0 && (
          <Select value={userFilter} onValueChange={(v) => setUserFilter(v)}>
            <SelectTrigger size="sm" className="min-w-[160px]" aria-label={t("history.filterByCollaborator")}>
              <SelectValue placeholder={t("history.filterByCollaborator")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("history.allCollaborators")}</SelectItem>
              {!teamScope && <SelectItem value="mine">{t("history.mySessions")}</SelectItem>}
              {scopedUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {!isKestioAdmin && !teamScope && hasPilot && onOpenTeamHistory && (
          <button type="button" onClick={onOpenTeamHistory} className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full px-3 text-xs font-semibold" style={{ background: "#EEEAF9", color: "#5A4BBE" }}>
            {t("history.teamInPilot")} →
          </button>
        )}
        {!isKestioAdmin && (
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger size="sm" className="flex-1 min-w-0" aria-label={t("history.filterByAgent")}>
              <SelectValue placeholder={t("history.filterByAgent")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("history.allAgents")}</SelectItem>
              {agentOptions.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  <span className="flex items-center gap-2">
                    <AgentIcon icon={agent.icon} size="sm" />
                    {agent.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}
          className="shrink-0 gap-1.5 text-xs"
        >
          {t("history.date")}
          <ArrowDown className="h-3.5 w-3.5 transition-transform" style={{ transform: sortOrder === "asc" ? "rotate(180deg)" : undefined }} />
        </Button>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-6">
        {/* Sessions List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">{t("history.sessions")}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {filteredSessions.map((session) => (
                <button
                  key={session.id}
                  className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                    selectedDetail?.id === session.id ? "bg-muted" : ""
                  }`}
                  onClick={() => { loadDetail(session.id); scrollToDetail(); }}
                >
                  <div className="flex items-center gap-3">
                    <AgentIcon icon={session.simulatorIcon} size="md" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{session.simulatorName}</p>
                      <p className="text-xs text-muted-foreground">
                        {session.userFirstName && userFilter !== "mine" && !(!canViewAccount) && (
                          <span className="font-medium text-slate-600">{session.userFirstName} {session.userLastName} — </span>
                        )}
                        {formatDate(session.createdAt)} — {formatDuration(session.durationInMinutes)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {session.simulatorType === "coach" ? (
                        <Badge variant="secondary" className="text-kestio-blue border-kestio-blue/30 text-xs">{t("history.coach")}</Badge>
                      ) : (
                        <span className={`font-bold ${getTotalScoreColor(session.feedbackTotalScore ?? 0)}`}>
                          {session.feedbackTotalScore != null ? session.feedbackTotalScore : "—"}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
              {filteredSessions.length === 0 && (
                <div className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">{noSessionMessage}</p>
                  {noSessionSubMessage && <p className="text-xs text-muted-foreground mt-1">{noSessionSubMessage}</p>}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Session Details */}
        <div className="lg:col-span-2" ref={detailContainerRef}>
          {loadingDetail ? (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <p className="text-sm text-muted-foreground">{t("history.loadingDetail")}</p>
              </CardContent>
            </Card>
          ) : detailError ? (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <p className="text-sm text-destructive text-center">{detailError}</p>
              </CardContent>
            </Card>
          ) : selectedDetail ? (
            <SessionDetailView session={selectedDetail} onNavigate={onNavigate} />
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center py-20">
                <p className="text-sm text-muted-foreground">{t("history.selectSession")}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile Layout - Liste */}
      <div className="lg:hidden space-y-2">
        {filteredSessions.map((session) => (
          <div
            key={session.id}
            className="bg-card border rounded-xl px-3 py-2.5 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => loadDetail(session.id, true)}
          >
            <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-2">
              {/* Row 1 */}
              <span className="row-span-2 self-center"><AgentIcon icon={session.simulatorIcon} size="sm" /></span>
              <p className="font-medium text-sm truncate">{session.simulatorName}</p>
              {session.simulatorType === "coach" ? (
                <Badge variant="secondary" className="text-kestio-blue border-kestio-blue/30 text-xs">{t("history.coach")}</Badge>
              ) : (
                <span className={`text-sm font-bold ${getTotalScoreColor(session.feedbackTotalScore ?? 0)}`}>
                  {session.feedbackTotalScore != null ? session.feedbackTotalScore : "—"}
                </span>
              )}
              <ChevronRight className="h-4 w-4 text-muted-foreground row-span-2 self-center" />
              {/* Row 2 */}
              <p className="text-xs text-muted-foreground">
                {session.userFirstName && userFilter !== "mine" && canViewAccount && (
                  <span className="font-medium text-slate-600">{session.userFirstName} {session.userLastName} — </span>
                )}
                {formatDate(session.createdAt)} — {formatDuration(session.durationInMinutes)}
              </p>
              <div />
            </div>
          </div>
        ))}
        {filteredSessions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">{noSessionMessage}</p>
            {noSessionSubMessage && <p className="text-xs text-muted-foreground mt-1">{noSessionSubMessage}</p>}
          </div>
        )}
      </div>

      </>)}{/* fin du wrapper historyTab === "training" */}

      {/* Ancien bloc groupé Parcours/Challenge — désactivé : on utilise désormais
          la même structure liste que « Libre » (cf. condition ci-dessus). */}
      {false && (historyTab === "parcours" || historyTab === "challenge") && (
        <div className="mt-4 space-y-5">
          {contextGroups.length === 0 ? (
            <div className="text-center text-muted-foreground py-16 border rounded-xl text-sm">
              {historyTab === "parcours" ? t("history.noParcoursSession") : t("history.noChallengeSession")}
            </div>
          ) : (
            contextGroups.map(([title, items]) => (
              <div key={title} className="overflow-hidden rounded-xl border">
                <div className="flex items-center justify-between gap-2 border-b bg-slate-50 px-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    {historyTab === "parcours"
                      ? <Award className="h-4 w-4 shrink-0" style={{ color: "#5A4BBE" }} />
                      : <Trophy className="h-4 w-4 shrink-0" style={{ color: "#926B00" }} />}
                    <span className="truncate font-semibold" style={{ color: "#384872" }}>{title}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">· {t("history.sessionCount", { count: items.length })}</span>
                  </div>
                  {historyTab === "challenge" ? (
                    (onOpenChallenge || onNavigate) && (
                      <button
                        onClick={() => {
                          const chId = items.find((s) => s.challengeId)?.challengeId;
                          if (chId && onOpenChallenge) onOpenChallenge(chId);
                          else onNavigate?.("challenge");
                        }}
                        className="shrink-0 text-xs font-medium hover:underline"
                        style={{ color: "#384872" }}
                      >
                        {t("history.viewChallenge")}
                      </button>
                    )
                  ) : (
                    onNavigate && (
                      <button
                        onClick={() => onNavigate("certification-paths")}
                        className="shrink-0 text-xs font-medium hover:underline"
                        style={{ color: "#384872" }}
                      >
                        {t("history.viewParcours")}
                      </button>
                    )
                  )}
                </div>
                <div className="divide-y">
                  {items.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => loadDetail(s.id, true)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50"
                    >
                      {s.simulatorIcon && (s.simulatorIcon.startsWith("http") || s.simulatorIcon.startsWith("data:"))
                        ? <img src={s.simulatorIcon} alt="" loading="lazy" className="h-7 w-7 shrink-0 rounded object-contain" />
                        : <span className="text-lg shrink-0">{s.simulatorIcon || "🎯"}</span>}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {s.simulatorName}
                          {historyTab === "parcours" && s.pathPhaseSequence != null && (
                            <span className="text-muted-foreground"> · {t("history.phaseN", { n: (s.pathPhaseSequence ?? 0) + 1 })}</span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(s.createdAt)} · {formatDuration(s.durationInMinutes)}</p>
                      </div>
                      {s.feedbackTotalScore != null && (
                        <span className="shrink-0 text-sm font-bold" style={{ color: "#384872" }}>{s.feedbackTotalScore}</span>
                      )}
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Composant liste des fiches Prep Meeting (brouillons + finalisées)
/**
 * Préparation de rendez-vous d'une personne de l'équipe, en LECTURE SEULE (décision D5 du
 * 30/09/2026) : la fiche telle qu'elle a été générée, sans reprise, chat ni suppression. Le serveur
 * (`coach-detail?team=1`) applique la règle de la liste et n'écrit rien.
 */
function TeamPrepMeetingDialog({ sessionId, ownerName, onClose }: { sessionId: string; ownerName: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof trainingApi.getCoachSessionDetail>> | null>(null);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let live = true;
    setDetail(null); setFailed(false);
    trainingApi.getCoachSessionDetail(sessionId, { team: true })
      .then((d) => { if (live) setDetail(d); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [sessionId, reload]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b px-5 py-3">
          <p className="flex-1 text-sm" style={{ color: "#384872" }}>{t("history.prepReadOnlyHint", { name: ownerName })}</p>
          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ background: "#EEEAF9", color: "#5A4BBE" }}>{t("history.prepReadOnlyBadge")}</span>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {failed ? (
            <div role="alert" className="flex flex-wrap items-center gap-3 py-4 text-sm" style={{ color: "#C0423F" }}>
              {t("common.loadError")}
              <button type="button" className="font-semibold underline" onClick={() => setReload((n) => n + 1)}>{t("common.retry")}</button>
            </div>
          ) : !detail ? (
            <p className="py-6 text-center text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : detail.sections && detail.sections.length > 0 ? (
            detail.sections.map((section, i) => (
              <section key={i} className="rounded-xl border p-4">
                <h3 className="mb-2 text-sm font-semibold" style={{ color: "#384872", fontFamily: "Sora, sans-serif" }}>{section.title}</h3>
                <div className="whitespace-pre-wrap text-sm" style={{ color: "#4B5573" }}>{section.content}</div>
              </section>
            ))
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">{detail.status === "draft" ? t("history.prepDraftNoBrief") : t("history.prepInProgress")}</p>
          )}
        </div>
        <div className="flex justify-end border-t px-5 py-3">
          <Button variant="ghost" onClick={onClose}>{t("common.close")}</Button>
        </div>
      </div>
    </div>
  );
}

function PrepMeetingHistoryList({
  items, loading, onOpen, onDelete, team,
}: {
  items: Array<{
    id: string;
    /** Mode équipe : la personne qui a préparé. */
    userName?: string;
    createdAt: string;
    updatedAt: string;
    status: "draft" | "completed";
    companyName: string | null;
    contactName: string | null;
    contactRole: string | null;
    meetingObjective: string | null;
  }>;
  loading: boolean;
  /** Mode équipe : `onOpen` ouvre la préparation en LECTURE seule (D5) ; `onDelete` absent (jamais la suppression d'autrui). */
  onOpen?: (id: string) => void;
  onDelete?: (id: string) => void;
  /** Historique de l'équipe (Pilot) : nom de la personne, état vide propre. */
  team?: boolean;
}) {
  const { t, i18n } = useTranslation();
  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-muted-foreground">{t("history.loadingPrepMeetings")}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed p-12 text-center">
        <div className="text-4xl mb-3">📋</div>
        <h3 className="text-base font-semibold mb-2" style={{ color: "#384872" }}>
          {team ? t("history.noTeamPrepMeeting") : t("history.noPrepMeetingYet")}
        </h3>
        {!team && (
          <p className="text-sm max-w-md mx-auto" style={{ color: "#4B5573" }}>
            <Trans i18nKey="history.prepMeetingEmptyHint" components={{ 1: <strong /> }} />
          </p>
        )}
      </div>
    );
  }

  const MEETING_OBJECTIVE_LABELS: Record<string, string> = {
    coldCall: t("history.prepObjectives.coldCall"),
    obtentionRdv: t("history.prepObjectives.obtentionRdv"),
    rdvDecouverte: t("history.prepObjectives.rdvDecouverte"),
    qualification: t("history.prepObjectives.qualification"),
    pitchDemo: t("history.prepObjectives.pitchDemo"),
    presentationOffre: t("history.prepObjectives.presentationOffre"),
    negotiation: t("history.prepObjectives.negotiation"),
    closing: t("history.prepObjectives.closing"),
    customerSuccess: t("history.prepObjectives.customerSuccess"),
    relance: t("history.prepObjectives.relance"),
  };

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const isDraft = item.status === "draft";
        const isComplete = !!(item.companyName && item.contactName);
        const titleParts: string[] = [];
        if (item.companyName) titleParts.push(item.companyName);
        if (item.contactName) titleParts.push(item.contactName);
        const title = titleParts.length > 0 ? titleParts.join(" — ") : t("history.untitledPrep");
        const objectiveLabel = item.meetingObjective ? (MEETING_OBJECTIVE_LABELS[item.meetingObjective] ?? item.meetingObjective) : null;

        return (
          <div
            key={item.id}
            onClick={onOpen ? () => onOpen(item.id) : undefined}
            className={`bg-white border rounded-xl p-4 flex items-start gap-3 ${onOpen ? "cursor-pointer hover:shadow-md transition-all" : ""}`}
          >
            <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0 text-xl" style={{ background: isDraft ? "#FEF3C7" : "#F3F0FF" }}>
              {isDraft ? "✏️" : "📋"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-bold text-sm md:text-base truncate" style={{ color: "#384872" }}>
                  {title}
                </h3>
                {isDraft ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
                    {t("history.draftBadge")}
                  </span>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "#D1FAE5", color: "#065F46" }}>
                    {t("history.finalizedBadge")}
                  </span>
                )}
              </div>
              <div className="text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1" style={{ color: "#6B7280" }}>
                {team && item.userName && <span className="font-semibold" style={{ color: "#384872" }}>{item.userName}</span>}
                {item.contactRole && <span>{item.contactRole}</span>}
                {objectiveLabel && <span>· {objectiveLabel}</span>}
                <span>· {new Date(item.updatedAt).toLocaleDateString(i18n.language === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
              {isDraft && !isComplete && (
                <p className="text-xs mt-2 italic" style={{ color: "#92400E" }}>
                  {t("history.draftIncompleteWarning")}
                </p>
              )}
            </div>
            {/* Bouton poubelle uniquement sur les brouillons — les préparations
                finalisées ne sont pas supprimables (fiche IA à préserver). */}
            {isDraft && onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(t("history.deleteDraftConfirm"))) onDelete(item.id);
                }}
                title={t("history.deleteDraftTooltip")}
                aria-label={t("history.deleteDraftTooltip")}
                className="h-8 w-8 rounded-full flex items-center justify-center self-center shrink-0 text-red-500 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            {onOpen && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 self-center" />}
          </div>
        );
      })}
    </div>
  );
}
